import {
  getBoardingPassengers,
  getDeboardingPassengers,
  getOnBoardPassengers,
  getPassengerCount,
  getStayingPassengers,
  usePassengerAuthentications,
} from '@backoffice-frontend/ad-passenger-list'
import { FccPassengerAuthenticationState } from '@backoffice-frontend/graphql'
import { useDebounce } from '@backoffice-frontend/hooks'
import { evaluateCabinSafetyCheckStatus } from '../../../evaluateCabinSafetyCheckStatus'
import { useUpcomingWaypoints } from './useUpcomingWaypoints'

export type Step = 'BOARDING' | 'CABIN_SAFETY_CHECK' | 'DEPARTURE'

export const useWaypointInfo = (
  vehicleId: string,
  isCabinSafetyConfirmed = false,
) => {
  const { waypoint, futureWaypoints } = useUpcomingWaypoints({ vehicleId })

  const {
    subscribeToPassengerAuthentications,
    isAllAuthenticationsCancelled,
    isAllPassengersCheckedIn,
  } = usePassengerAuthentications()

  subscribeToPassengerAuthentications(waypoint)

  const boardingPassengers = waypoint && getBoardingPassengers(waypoint)
  const deboardingPassengers = waypoint ? getDeboardingPassengers(waypoint) : []

  const { hasPickup, isStandaloneCabinSafetyCheck } =
    evaluateCabinSafetyCheckStatus(waypoint)

  const cancelledPassengers = boardingPassengers?.filter(
    passenger =>
      passenger.tripIsCancelled ||
      passenger.authenticationState ===
        FccPassengerAuthenticationState.Cancelled,
  )

  const hasCancelledPassengers = (cancelledPassengers?.length ?? 0) > 0
  const hasOnlyCancelledPassengers =
    hasCancelledPassengers &&
    boardingPassengers?.length === cancelledPassengers?.length

  const isBoardingComplete = useDebounce(
    hasOnlyCancelledPassengers || isAllPassengersCheckedIn,
    // debouncing three seconds to show the success state for a short time
    3 * 1000,
  )
  const isReadyForDeparture = useDebounce(
    isAllAuthenticationsCancelled && deboardingPassengers.length === 0,
    // debouncing three seconds to show the no-show state for a short time
    3 * 1000,
  )

  const isReadyDepartureStep = isCabinSafetyConfirmed || isReadyForDeparture

  const activeStep: Step = isReadyDepartureStep
    ? 'DEPARTURE'
    : isStandaloneCabinSafetyCheck || !hasPickup || isBoardingComplete
      ? 'CABIN_SAFETY_CHECK'
      : 'BOARDING'

  const onBoardPassengers = getOnBoardPassengers([
    ...(waypoint ? [waypoint] : []),
    ...futureWaypoints,
  ])

  const stayingPassengers = waypoint
    ? getStayingPassengers(waypoint, futureWaypoints)
    : []

  const nonCancelledBoardedPassengers =
    boardingPassengers?.filter(
      passenger =>
        passenger.authenticationState !==
        FccPassengerAuthenticationState.Cancelled,
    ) ?? []

  const expectedPassengerCount = isStandaloneCabinSafetyCheck
    ? getPassengerCount(onBoardPassengers)
    : getPassengerCount([
        ...nonCancelledBoardedPassengers,
        ...stayingPassengers,
      ])

  return {
    isStandaloneCabinSafetyCheck,
    isReadyForDeparture,
    activeStep,
    expectedPassengerCount,
    waypoint,
    futureWaypoints,
  }
}
